import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { ArrowRight } from "lucide-react";
import { formatUSD, progressPercent } from "@/lib/format";
import { optimizedImage } from "@/lib/image";

const assetMap: Record<string, string> = import.meta.glob("/src/assets/*", {
  eager: true,
  import: "default",
});
const resolveAsset = (path?: string | null) => (path && assetMap[path]) || path || "";

export function ProjectsGrid() {
  const { data: projects } = useQuery({
    queryKey: ["home_projects"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("id,slug,title,short_description,hero_image,goal_cents,raised_cents,status,completed_at,accepts_donations,categories(name,slug)")
        .not("hero_image", "is", null)
        .not("status", "in", "(upcoming,draft)")
        .order("completed_at", { ascending: false, nullsFirst: false })
        .limit(6);
      if (error) throw error;
      return data ?? [];
    },
    retry: 4,
    retryDelay: (attempt) => Math.min(800 * 2 ** attempt, 5000),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  return (
    <section className="py-24">
      <div className="container mx-auto px-4">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <span className="text-sm font-semibold uppercase tracking-widest text-primary">
              Our projects
            </span>
            <h2 className="mt-3 font-display text-4xl font-semibold leading-tight md:text-5xl text-balance">
              Where your gift goes to work
            </h2>
            <p className="mt-4 text-lg text-muted-foreground text-pretty">
              Raise Sri Lanka is powered by the global Sri Lankan community and a
              dedicated volunteer team delivering impact across the country.
            </p>
          </div>
          <Link
            to="/projects"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:gap-3 transition-all"
          >
            View all projects <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {projects?.map((p) => {
            const pct = progressPercent(p.raised_cents, p.goal_cents);
            const isCompleted = p.status === "completed";
            const isClosed = isCompleted || p.accepts_donations === false;
            return (
              <article
                key={p.id}
                className="group flex flex-col overflow-hidden rounded-2xl bg-card shadow-card transition-all hover:-translate-y-1 hover:shadow-soft"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={optimizedImage(resolveAsset(p.hero_image), { width: 600, height: 450 })}
                    alt={p.title}
                    width={600}
                    height={450}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  {p.categories?.name && (
                    <span className="absolute left-4 top-4 rounded-full bg-background/95 px-3 py-1 text-xs font-medium text-primary backdrop-blur">
                      {p.categories.name}
                    </span>
                  )}
                  {isCompleted && (
                    <span className="absolute right-4 top-4 rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
                      Completed
                    </span>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="font-display text-2xl font-semibold leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
                    {p.short_description}
                  </p>

                  {!isCompleted && p.goal_cents > 0 && (
                    <div className="mt-5">
                      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-primary transition-all duration-700"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <div className="mt-2 flex justify-between text-xs">
                        <span className="font-semibold text-foreground">
                          {formatUSD(p.raised_cents)}{" "}
                          <span className="font-normal text-muted-foreground">
                            of {formatUSD(p.goal_cents)}
                          </span>
                        </span>
                        <span className="font-semibold text-primary">{pct}%</span>
                      </div>
                    </div>
                  )}
                  {isCompleted && p.completed_at && (
                    <p className="mt-5 text-xs text-muted-foreground">
                      Delivered {new Date(p.completed_at).toLocaleDateString(undefined, { month: "short", year: "numeric" })}
                    </p>
                  )}
                  <div className="mt-5 flex items-center gap-2">
                    {isClosed ? (
                      <Link
                        to={`/projects/${p.slug}`}
                        className="inline-flex flex-1 items-center justify-center rounded-full border-2 border-border px-4 py-2 text-sm font-semibold transition-colors hover:border-primary/40"
                      >
                        View story
                      </Link>
                    ) : (
                      <>
                        <Link
                          to={`/donate?project_id=${p.id}&type=one_time`}
                          className="inline-flex flex-1 items-center justify-center rounded-full bg-accent px-4 py-2 text-sm font-semibold text-accent-foreground transition-colors hover:bg-accent/90"
                        >
                          Sponsor this project
                        </Link>
                        <Link
                          to={`/projects/${p.slug}`}
                          className="inline-flex items-center justify-center rounded-full border-2 border-border px-4 py-2 text-sm font-semibold transition-colors hover:border-primary/40"
                        >
                          Details
                        </Link>
                      </>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
